import { createSlice } from "@reduxjs/toolkit";
import _ from "lodash";
import { bulkEditTaskInFirebase } from "../../actions/TodoManipulation/bulkEditAction";


const projectSlice = createSlice({
    name: "projects",
    initialState: [],
    reducers: {
        setProjects(state, action) {
            return action.payload;
        },
        updateProjectChildren(state, action) {
            const { parentIdx, updatedChildren } = action.payload;
            return {
                ...state,
                [parentIdx]: {
                    ...state[parentIdx],
                    children: updatedChildren,
                },
            };
        },
        combineTwoProjects(state, action) {
            const { sourceIndex, destinationProjectId } = action.payload;

            const destinationIndex = state.findIndex(
                (project) => project.$id === destinationProjectId
            );

            if (sourceIndex !== -1 && destinationIndex !== -1) {
                const updatedState = [...state];
                const sourceProject = { ...updatedState[sourceIndex] };
                const destinationProject = { ...updatedState[destinationIndex] };

                // Append sourceProject as a child of destinationProject
                destinationProject.children = [
                    ...(destinationProject.children || []),
                    sourceProject,
                ];

                // Replace destination project with updated version
                updatedState[destinationIndex] = destinationProject;
                // Remove sourceProject from its previous position
                updatedState.splice(sourceIndex, 1);

                return updatedState;
            }
        },

        sortProjectTasks(state, action) {
            const { projectId, oldIndex, newIndex } = action.payload;

            const projectIndex = state.findIndex(
                (project) => project.$id === projectId
            );

            if (projectIndex !== -1) {
                const updatedState = [...state];
                const project = { ...updatedState[projectIndex] };
                const sortedChildren = [...project.children];
                const movedTask = sortedChildren.splice(oldIndex, 1)[0];
                sortedChildren.splice(newIndex, 0, movedTask);

                project.children = sortedChildren;
                updatedState[projectIndex] = project;

                return updatedState;
            }

            return state;
        },
        moveTaskToAnotherProject(state, action) {
            const {
                oldProjectId,
                oldTaskIdx,
                newProjectId,
                newTaskIdx,
                movedTaskId,
            } = action.payload;

            const oldProjectIdx = state.findIndex(
                (project) => project.$id === oldProjectId
            );
            const newProjectIdx = state.findIndex(
                (project) => project.$id === newProjectId
            );
            const movedTask = state[oldProjectIdx]?.children?.find(
                (task) => task.$id === movedTaskId
            );

            if (movedTask) {
                const updatedOldChildren = [...state[oldProjectIdx].children];
                updatedOldChildren.splice(oldTaskIdx, 1);
                state[oldProjectIdx].children = updatedOldChildren;

                movedTask.parent.$ref = state[newProjectIdx].$id;

                const updatedDestinationChildren = [...state[newProjectIdx].children];
                updatedDestinationChildren.splice(newTaskIdx, 0, movedTask);
                state[newProjectIdx].children = updatedDestinationChildren;
            } else {
                console.error("Moved task not found in the project's children.");
            }
        },
        makeSubprojectRootProject(state, action) {
            const { oldProjectId, movedTaskId } = action.payload;

            const oldProjectIdx = state.findIndex(
                (project) => project.$id === oldProjectId
            );

            const movedSubProjectIndex = state[oldProjectIdx]?.children?.findIndex(
                (task) => task.$id === movedTaskId
            );

            if (movedSubProjectIndex === -1) {
                return state;
            }

            const updatedState = _.cloneDeep(state);

            const movedSubProject = {
                ...updatedState[oldProjectIdx].children[movedSubProjectIndex],
                parent: null,
            };
            if (movedSubProject.type === "1") {
                return state;
            }
            const updatedOldProjectChildren = updatedState[
                oldProjectIdx
            ].children.filter((task) => task.$id !== movedTaskId);

            updatedState[oldProjectIdx].children = updatedOldProjectChildren;
            updatedState.splice(oldProjectIdx + 1, 0, movedSubProject);

            return updatedState;
        },
        combineTasks(state, action) {
            const {
                sourceTaskId,
                sourceRootProjectId,
                destinationRootProjectId,
                destinationSubprojectId,
            } = action.payload;
            const updatedState = _.cloneDeep(state);
            const sourceRootProjectIndex = state.findIndex(
                (project) => project.$id === sourceRootProjectId
            );
            const sourceTaskIndex = state[sourceRootProjectIndex].children.findIndex(
                (task) => task.$id === sourceTaskId
            );
            const destinationRootProjectIndex = state.findIndex(
                (project) => project.$id === destinationRootProjectId
            );

            const destinationSubprojectIndex = state[
                destinationRootProjectIndex
            ].children.findIndex(
                (subProject) => subProject.$id === destinationSubprojectId
            );
            if (
                state[destinationRootProjectIndex].children[destinationSubprojectIndex]
                    .children.length === 0
            ) {
                const destinationRootProjectIndex = state.findIndex(
                    (project) => project.$id === destinationRootProjectId
                );

                const uniqueId = (Math.floor(Math.random() * 9000) + 1000).toString();
                const newSubproject = {
                    $id: (Math.floor(Math.random() * 9000) + 1000).toString(),
                    id: uniqueId,
                    title: "New Subproject",
                    children: [
                        updatedState[sourceRootProjectIndex].children[sourceTaskIndex],
                        updatedState[destinationRootProjectIndex].children[
                        destinationSubprojectIndex
                        ],
                    ],
                    color: { hexString: "#80C3E9" },
                    type: "0",
                    parent: uniqueId,
                };


                //Remove task it drops on from destination and adds new at that index
                updatedState[destinationRootProjectIndex].children.splice(
                    destinationSubprojectIndex,
                    1,
                    newSubproject
                );
                // Remove the dragged task from source
                updatedState[sourceRootProjectIndex].children.splice(
                    sourceTaskIndex,
                    1
                );

                return updatedState;
            } else {
                const draggedTask = {
                    ...updatedState[sourceRootProjectIndex].children[sourceTaskIndex],
                    parent: destinationSubprojectId,
                };

                const updatedSourceProjectChildren = updatedState[
                    sourceRootProjectIndex
                ].children.filter((task) => task.$id !== sourceTaskId);
                const updatedDestinationSubprojectChildren = [
                    ...updatedState[destinationRootProjectIndex].children[
                        destinationSubprojectIndex
                    ].children,
                    draggedTask,
                ];

                // Update the destination subproject's children
                updatedState[destinationRootProjectIndex].children[
                    destinationSubprojectIndex
                ].children = updatedDestinationSubprojectChildren;

                // Remove the dragged task from its source location
                updatedState[sourceRootProjectIndex].children =
                    updatedSourceProjectChildren;

                return updatedState;
            }
        },
        completeTask(state, action) {
            const { taskId } = action.payload;

            const updatedState = _.cloneDeep(state);

            function findAndCompleteTask(tasks) {
                for (let i = 0; i < tasks.length; i++) {
                    const currentTask = tasks[i];

                    if (currentTask.id === taskId) {
                        const updatedTask = { ...currentTask, isCompleted: true };

                        tasks[i] = updatedTask;

                        return true;
                    }

                    if (currentTask.children) {
                        if (findAndCompleteTask(currentTask.children)) {
                            return true;
                        }
                    }
                }

                return false;
            }

            for (const project of updatedState) {
                if (findAndCompleteTask(project.children)) {
                    return updatedState;
                }
            }

            return state;
        },
        addAiTasks(state, action) {
            const { newProjectTitle, tasksJson, projectId } = action.payload;
            let subTasks = tasksJson.tasks.map(task => task.title);

            //// Create a new task
            //const newProject = {
            //    $id: (Math.floor(Math.random() * 9000) + 1000).toString(),
            //    id: (Math.floor(Math.random() * 9000) + 1000).toString(),
            //    title: newProjectTitle,
            //    children: [],
            //    type: "0",
            //    parent: null,
            //    isCompleted: false,
            //    color: null,
            //};

            //// Add tasks to the new project
            //const newTasks = tasksJson.tasks.map((task, index) => ({
            //    $id: (Math.floor(Math.random() * 9000) + 1000).toString(),
            //    id: (Math.floor(Math.random() * 9000) + 1000).toString(),
            //    title: task.title,
            //    children: null,
            //    type: "1",
            //    parent: newProject.id,
            //    isCompleted: false,
            //    color: { hexString:task.color.hexString || "#80C3E9" }, 
            //}));

            //newProject.children = newTasks;

            //// Clone the state and add the new project
            //const updatedState = _.cloneDeep(state);
            //updatedState.push(newProject);

            // Make current task a sub project

            // create an array of sub tasks

            // dispatch BulkEdit action
            const json = {
                id: projectId,
                subtasksToAdd: subTasks
            };

            dispatch(bulkEditTaskInFirebase(json))

            return updatedState;
        },
        findTodoById(state, action) {
            const { id } = action.payload;

            const findItem = (projects, id) => {
                for (const project of projects) {
                    if (project.$id === id) {
                        return project;
                    }
                    if (project.children) {
                        const foundItem = findItem(project.children, id);
                        if (foundItem) {
                            return foundItem;
                        }
                    }
                }
                return null;
            };

            return findItem(state, id);
        },

    },
});

export const {
    setProjects,
    updateProjectChildren,
    sortProjectTasks,
    moveTaskToAnotherProject,
    combineTwoProjects,
    makeSubprojectRootProject,
    combineTasks,
    completeTask,
    addAiTasks,
    findTodoById
} = projectSlice.actions;

export default projectSlice.reducer;
